import React, { useEffect, useContext, useMemo } from 'react';
import Global from '../../styles/global';
import {
  Appointments,
  Banner,
  ConfirmEmailAlert,
  TermsAndPolicySession,
  TokboxVideoCall,
} from './components';
import { GeneralContext } from '../../shared/contexts/GeneralContext.context';
import { useQuery, useTranslation } from '../../shared/hooks';
import {
  getCookie,
  setCookie,
  deleteCookie,
} from '../../shared/helpers/cookies';
import { apiService } from '../../services';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';

const LOGIN_REDIRECT_GUARD_KEY = 'loginRedirectAt';
const LOGIN_REDIRECT_GUARD_WINDOW_MS = 10000;

const Home = () => {
  const { setUserData, isLogged, setIsLoading, userData, client } =
    useContext(GeneralContext);
  const { theme, lang } = useParams();
  const { idiom, setIdiom } = useTranslation({});
  const navigate = useNavigate();
  const query = useQuery();
  const location = useLocation();
  let pathLang = window.location.pathname.split('/')[2];
  const isGenericDomain =
    window.location.hostname === import.meta.env.VITE_DOMAIN_GENERIC;
  const showConfirmEmailAlert = userData?.email_verified === false;

  const cleanAmosTeledocAuth = () => {
    sessionStorage.removeItem('amosTeladocApiKey');
    sessionStorage.removeItem('amosTeladocToken');
    sessionStorage.removeItem('amosTeladocSessionId');
  };

  setCookie(
    'fullURL',
    window.location.protocol +
      '//' +
      window.location.host +
      window.location.pathname
  );

  const convertLang = (keyLang) => {
    switch (keyLang) {
      case 'es':
        return 'es_ES';
      case 'pt':
      case 'pt_BR':
        return 'pt_BR';
      case 'fr':
        return 'fr_FR';
      case 'de':
        return 'de_DE';
      case 'it':
        return 'it_IT';
      case 'en':
      case 'en_GB':
        return 'en_US';
      default:
        return 'en_US';
    }
  };

  const handleEndCall = async (id) => {
    try {
      setIsLoading(true);
      const clientToken = getCookie('amosToken');
      const bodyObj = {
        clientToken,
        email: client.email,
        firstName: client.name,
        lastName: client.surname,
        language: client.language,
      };
      await apiService.amos.amosEndVideoCall(id, bodyObj);

      setIsLoading(false);
      cleanAmosTeledocAuth();
      navigate('/');
    } catch (err) {
      setIsLoading(false);
      console.log(err);
    }
  };

  useMemo(() => {
    if (pathLang) {
      if (pathLang.length == 2) {
        setIdiom(convertLang(pathLang));
      }
      return;
    }
    if (lang) {
      setIdiom(convertLang(lang));
      return;
    }
  }, [pathLang, lang]);

  useEffect(() => {
    localStorage.setItem('theme', theme ? theme : '');
  }, [theme]);

  const redirectToLoginOnce = (url) => {
    const now = Date.now();
    const lastRedirectAt = Number(
      sessionStorage.getItem(LOGIN_REDIRECT_GUARD_KEY)
    );
    const redirectedRecently =
      Number.isFinite(lastRedirectAt) &&
      now - lastRedirectAt < LOGIN_REDIRECT_GUARD_WINDOW_MS;

    if (redirectedRecently) {
      return;
    }

    sessionStorage.setItem(LOGIN_REDIRECT_GUARD_KEY, String(now));
    window.location.href = url;
  };

  const homeBehavior = () => {
    try {
      if (idiom != undefined && idiom.length >= 2) {
        document.documentElement.lang = idiom.substr(0, 2);
      }
      let queryParam1 = 'lang';
      let queryParam2 = 'ui_locales';
      let lanquagecode1 = idiom.substr(0, 2);
      let lanquagecode2 =
        idiom.substr(0, 2) == 'pt'
          ? 'pt-PT'
          : idiom.substr(0, 2) == 'fr'
            ? 'fr-FR'
            : idiom.substr(0, 2);
      if (!isLogged && navigator.onLine && !isGenericDomain) {
        if (window.location.hostname === import.meta.env.VITE_DOMAIN_WWIIS) {
          const redirectURL = getCookie('brandCookieURL');
          if (redirectURL) {
            deleteCookie('brandCookieURL');
            window.location.href = redirectURL;
          } else {
            redirectToLoginOnce(
              '/login?connection=PartnerSSO&' +
                queryParam1 +
                '=' +
                lanquagecode1 +
                '&' +
                queryParam2 +
                '=' +
                lanquagecode2
            );
          }
        } else {
          redirectToLoginOnce(
            '/login?' +
              queryParam1 +
              '=' +
              lanquagecode1 +
              '&' +
              queryParam2 +
              '=' +
              lanquagecode2
          );
        }
      } else if (!isLogged && !navigator.onLine) {
        console.log(
          'User not logged in but OFFLINE - staying on page with cached data'
        );
      }

      if (
        window.location.hostname === import.meta.env.VITE_DOMAIN_WWIIS &&
        navigator.onLine
      ) {
        const urlParams = new URLSearchParams(window.location.search);

        // Get query parameters
        const brand = urlParams.get('Brand');
        const policyNumber = urlParams.get('policyNumber');
        if (brand && policyNumber) {
          redirectToLoginOnce(
            `/login?connection=PartnerSSO&${queryParam1}=${lanquagecode1}&${queryParam2}=${lanquagecode2}`
          );
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    homeBehavior();
  }, []);

  if (query.get('type')) window.scrollTo(0, 750);

  if (query.get('type') === 'videoCall') {
    const amosTeladocApiKey = getCookie('amosTeladocApiKey');
    const amosTeladocToken = getCookie('amosTeladocToken');
    const amosTeladocSessionId = getCookie('amosTeladocSessionId');
    const amosTeladocId = getCookie('amosTeladocId');

    return (
      <ContainerFullScreen>
        <TokboxVideoCall
          apiKey={amosTeladocApiKey}
          token={amosTeladocToken}
          sessionId={amosTeladocSessionId}
          amosTeladocId={amosTeladocId}
          onEndCall={handleEndCall}
        />
      </ContainerFullScreen>
    );
  }

  return (
    <>
      <Global />
      {showConfirmEmailAlert && <ConfirmEmailAlert />}
      {!showConfirmEmailAlert &&
        (userData?.email_verified || isGenericDomain) && (
          <>
            <Banner />
            {userData?.email_verified && (
              <>
                {/* <TermsAndPolicySession /> */}
                {window.location.hostname !==
                  import.meta.env.VITE_DOMAIN_GENERIC && <Appointments />}
              </>
            )}
          </>
        )}
      {/* {userData?.email_verified === undefined && (
        <>
          <Banner />
          <Appointments />
        </>
      )} */}
    </>
  );
};

export default Home;

const ContainerFullScreen = styled('div')({
  background: '#000',
  position: 'absolute',
  width: '100%',
  height: '100vh',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
});
